import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography, useTheme, Stack, useMediaQuery } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";

const LicenseNotice = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const { license, appConstants } = useSelector((state) => state.service);

  const status = license?.licenseStatus;
  const LICENSE_STATUS = appConstants?.allTypes?.LICENSE_STATUS || {};

  if (!license || license.isLoading || !license.lastFetchedAt) {
    return null;
  }

  const isExpired =
    (Boolean(LICENSE_STATUS?.EXPIRED) && status === LICENSE_STATUS.EXPIRED) ||
    status === "Expired";

  const isGrace =
    Boolean(LICENSE_STATUS?.GRACE) && status === LICENSE_STATUS.GRACE;

  const isActive =
    Boolean(LICENSE_STATUS?.ACTIVE) && status === LICENSE_STATUS.ACTIVE;

  const handleClick = () => {
    navigate("/settings");
  };

const getResponsiveText = (fullText, mediumText, shortText) => {
  if (isXs) return null;       // icon only
  if (isSm) return shortText;  // small screen
  return fullText;             // large screen
};

  const renderNotice = (bgColor, fullText, mediumText, shortText, icon) => {
    const text = getResponsiveText(fullText, mediumText, shortText);
    const Icon = icon || WarningAmberIcon;

    return (
      <Box
        sx={{ textAlign: { xs: "left", sm: "right" }, minWidth: 0 }}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{
            px: 1.2,
            py: 0.35,
            borderRadius: 999,
            bgcolor: bgColor,
            color: theme.palette.common.white,
            maxWidth: { xs: "100%", md: 560 },
            width: "fit-content",
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <Icon sx={{ fontSize: 18 }} />

          {text && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  // Grace period notice
  if (isGrace) {
    return renderNotice(
      theme.palette.error.main,
      "License expired, application will shut down soon. Please update your license.",
      "License expired. Update required.",
      "License expired"
    );
  }

  if (isExpired) {
    return null;
  }

  if (license?.isExpiringSoon) {
    const days = Number(license?.daysUntilExpiry);
    const safeDays = Number.isFinite(days) && days > 0 ? days : 0;
    const daysText = `${safeDays} day${safeDays === 1 ? "" : "s"}`;

    return renderNotice(
      theme.palette.warning.main,
      `License will expire soon (${daysText} remaining). Please update your license.`,
      `License expiring soon (${daysText})`,
      "Expiring soon"
    );
  }

  // Informational notice (before expiring-soon warning window starts)
  if (!isActive) return null;
  const days = Number(license?.daysUntilExpiry);
  if (!Number.isFinite(days) || days <= 0) return null;
  const daysText = `${days} day${days === 1 ? "" : "s"}`;
  const expiryDate = license?.licenseInfo?.expiryDate;
  const formattedExpiryDate = expiryDate
    ? new Date(expiryDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;
  if (!formattedExpiryDate) return null;

  return renderNotice(
    theme.palette.info.main,
    `License expires on ${formattedExpiryDate} (${daysText} left).`,
    `Expires on ${formattedExpiryDate} (${daysText})`,
    "License info",
    InfoOutlinedIcon
  );
};

export default LicenseNotice;